import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import useGeolocation from './useGeolocation';

/**
 * Hook to manage user location - detects location and updates user profile if missing
 * Also provides location data for filtering services
 */
const useUserLocation = () => {
  const { user, updateUser, isAuthenticated } = useAuth();
  const { location: geoLocation, address, country: geoCountry, getCurrentLocation, loading: geoLoading, error: geoError } = useGeolocation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [locationUpdated, setLocationUpdated] = useState(false);

  // Check if user needs location update
  const needsLocationUpdate = user && (
    !user.latitude || 
    !user.longitude || 
    !user.country || 
    !user.ip_address
  );

  // Update user location in database
  const updateUserLocation = async (locationData, addressData, countryData = null) => {
    if (!isAuthenticated || !user || isUpdating) return;

    setIsUpdating(true);
    try {
      // Parse address to get city and state
      const addressParts = addressData?.split(', ') || [];
      const city = addressParts[0] || null;
      const state = addressParts[1] || null;
      // Use country from geocoding result (most accurate), fallback to user's stored country
      const country = countryData || user?.country || null;
      
      console.log('Updating user location with country:', { 
        countryFromGeocoding: countryData, 
        userCountry: user?.country, 
        finalCountry: country 
      });

      // Prepare location update data
      const updateData = {
        country: country,
        city: city,
        state: state,
        latitude: locationData?.latitude || null,
        longitude: locationData?.longitude || null,
      };

      // Update via profile endpoint
      try {
        await authService.updateProfile(updateData);
        
        // Update local user state with location data
        const updatedUser = {
          ...user,
          ...updateData,
        };
        
        updateUser(updatedUser);
        setLocationUpdated(true);
      } catch (error) {
        console.error('Error updating user location:', error);
        // Still update local state for filtering purposes
        const updatedUser = {
          ...user,
          ...updateData,
        };
        updateUser(updatedUser);
      }
    } catch (error) {
      console.error('Error updating user location:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Auto-detect location on mount and periodically refresh (works for both authenticated and non-authenticated users)
  useEffect(() => {
    // Always try to detect location on mount for better accuracy, even if user has stored location
    // This ensures we have the most current location for the search bar
    if (!geoLoading && !isUpdating) {
      // Immediately try to get location (force fresh location)
      getCurrentLocation();
    }
    
    // Refresh location when page becomes visible (user might have changed VPN)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !geoLoading && !isUpdating) {
        console.log('Page became visible, refreshing location...');
        getCurrentLocation();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Refresh location every 5 minutes to catch VPN/location changes
    const refreshInterval = setInterval(() => {
      if (!geoLoading && !isUpdating) {
        console.log('Periodic location refresh to detect changes...');
        getCurrentLocation();
      }
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Update user when location is detected (always update if location changed significantly)
  useEffect(() => {
    if (geoLocation && address && !isUpdating) {
      // Check if location changed significantly (more than 10km difference)
      const hasStoredLocation = user?.latitude && user?.longitude;
      let shouldUpdate = false;
      
      if (!hasStoredLocation || needsLocationUpdate) {
        // No stored location or missing data - always update
        shouldUpdate = !locationUpdated;
      } else {
        // Check if location changed significantly (more than 10km)
        const storedLat = parseFloat(user.latitude);
        const storedLng = parseFloat(user.longitude);
        const newLat = parseFloat(geoLocation.latitude);
        const newLng = parseFloat(geoLocation.longitude);
        
        // Calculate distance using Haversine formula
        const R = 6371; // Earth's radius in km
        const dLat = (newLat - storedLat) * Math.PI / 180;
        const dLng = (newLng - storedLng) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(storedLat * Math.PI / 180) * Math.cos(newLat * Math.PI / 180) *
                  Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        
        // Update if location changed by more than 10km
        if (distance > 10) {
          console.log(`Location changed significantly: ${distance.toFixed(2)}km away. Updating database...`);
          shouldUpdate = true;
          setLocationUpdated(false); // Reset to allow update
        }
      }
      
      if (shouldUpdate) {
        // Pass country from geolocation hook
        updateUserLocation(geoLocation, address, geoCountry);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoLocation?.latitude, geoLocation?.longitude, address, geoCountry, isUpdating]);

  // Force refresh location on mount to ensure we get fresh location (not cached)
  // This is especially important when switching browsers or after VPN changes
  useEffect(() => {
    // Small delay to ensure component is fully mounted
    const timer = setTimeout(() => {
      if (!geoLoading) {
        console.log('Force refreshing location on mount to get fresh location...');
        getCurrentLocation();
      }
    }, 500);
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Get location for service filtering and display
  const getLocationForFiltering = () => {
    // Priority 1: Always use detected geolocation if available (most current, especially after VPN change)
    // This ensures every browser gets fresh location, not cached/stored data
    // IMPORTANT: Only return location if we have coordinates AND country (for proper filtering)
    if (geoLocation?.latitude && geoLocation?.longitude && address) {
      // Parse address into city and state
      const addressParts = address.split(', ');
      const parsedCity = addressParts[0] || null;
      const parsedState = addressParts[1] || null;
      
      // Get country - prioritize geoCountry (from reverse geocoding), fallback to user's stored country
      const detectedCountry = geoCountry || user?.country || null;
      
      // Only return location if we have country (critical for filtering)
      if (detectedCountry) {
        const location = {
          latitude: parseFloat(geoLocation.latitude),
          longitude: parseFloat(geoLocation.longitude),
          city: parsedCity,
          state: parsedState,
          country: detectedCountry,
          address: address,
        };
        // console.log('Using detected geolocation for filtering (fresh location from browser):', {
        //   ...location,
        //   geoCountry: geoCountry,
        //   userCountry: user?.country,
        //   finalCountry: detectedCountry
        // });
        return location;
      } else {
        console.warn('Location detected but country not available yet, waiting...', {
          hasGeoCountry: !!geoCountry,
          hasUserCountry: !!user?.country
        });
        return null; // Don't return location without country
      }
    }
    
    // Priority 2: Use stored user location if available (fallback only when geolocation not available)
    // Note: This should rarely be used since we force fresh geolocation on mount
    if (user?.latitude && user?.longitude) {
      const location = {
        latitude: parseFloat(user.latitude),
        longitude: parseFloat(user.longitude),
        city: user.city,
        state: user.state,
        country: user.country,
        address: user.city && user.state ? `${user.city}, ${user.state}` : 
                 user.city || user.state || user.country || null,
      };
      console.log('Using stored user location for filtering (fallback - geolocation not available):', location);
      return location;
    }
    
    console.log('No location available for filtering - waiting for geolocation...');
    return null;
  };

  return {
    userLocation: getLocationForFiltering(),
    needsUpdate: needsLocationUpdate,
    isUpdating,
    loading: geoLoading || isUpdating,
    error: geoError,
    updateLocation: () => {
      getCurrentLocation().then(() => {
        if (geoLocation && address) {
          updateUserLocation(geoLocation, address);
        }
      });
    },
  };
};

export default useUserLocation;

